import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme, garnetRed } from '../../design-system/theme';
import { RegionSelector } from './RegionSelector';
import { NotificationBell } from './NotificationBell';
import { TimezonePicker } from '../TimezonePicker';
// Note: TopHeader uses custom styled components instead of adminShared.ts
// because the header UI patterns (sticky nav, dropdown menus, search modal)
// are significantly different from admin page layouts.

const HeaderContainer = styled.header`
  height: 64px;
  background: white;
  border-bottom: 1px solid var(--ds-border, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const MobileMenuButton = styled.button`
  display: none;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
  }

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 24px;
    height: 24px;
    color: var(--ds-text-secondary, #565a74);
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--ds-text-secondary, #565a74);

  @media (max-width: 600px) {
    display: none;
  }
`;

const BreadcrumbLink = styled(Link)`
  color: var(--ds-text-secondary, #565a74);
  text-decoration: none;

  &:hover {
    color: var(--ds-primary, #363a50);
  }
`;

const BreadcrumbCurrent = styled.span`
  color: var(--ds-text-primary, #212435);
  font-weight: 500;
`;

const BreadcrumbSeparator = styled.span`
  color: var(--ds-text-subtle, #8b8fa9);
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--ds-border, #e2e8f0);
  border-radius: 8px;
  background: var(--ds-surface-muted, #f5f7fa);
  color: var(--ds-text-secondary, #565a74);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--ds-primary, #363a50);
    background: white;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  kbd {
    padding: 2px 6px;
    background: white;
    border: 1px solid var(--ds-border, #e2e8f0);
    border-radius: 4px;
    font-size: 11px;
    font-family: inherit;
  }

  @media (max-width: 768px) {
    padding: 8px;
    
    span, kbd {
      display: none;
    }
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--ds-border, #e2e8f0);
  margin: 0 8px;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
    border-color: var(--ds-border, #e2e8f0);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${theme.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-primary, #212435);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    display: none;
  }
`;

const DropdownArrow = styled.span<{ $open: boolean }>`
  display: flex;
  transition: transform 0.2s ease;
  transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0)'};
  color: var(--ds-text-secondary, #565a74);

  @media (max-width: 600px) {
    display: none;
  }
`;

const Dropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  min-width: 240px;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  background: ${theme.gradientPrimary};
  color: white;
`;

const DropdownUserName = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
`;

const DropdownEmail = styled.div`
  font-size: 13px;
  opacity: 0.85;
`;

const DropdownRole = styled.span`
  display: inline-block;
  margin-top: 8px;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownMenu = styled.div`
  padding: 8px 0;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  color: var(--ds-text-primary, #212435);
  text-decoration: none;
  font-size: 14px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
  }

  svg {
    width: 18px;
    height: 18px;
    color: var(--ds-text-secondary, #565a74);
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: var(--ds-text-primary, #212435);
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: ${garnetRed[1]};
    color: ${garnetRed[5]};

    svg {
      color: ${garnetRed[5]};
    }
  }

  svg {
    width: 18px;
    height: 18px;
    color: var(--ds-text-secondary, #565a74);
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--ds-border, #e2e8f0);
  margin: 4px 0;
`;

// Search Modal (simplified for now)
const SearchModal = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$open ? 'flex' : 'none'};
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 1000;
`;

const SearchBox = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: none;
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: var(--ds-text-subtle, #8b8fa9);
  }
`;

const SearchHint = styled.div`
  padding: 12px 20px;
  background: var(--ds-surface-muted, #f5f7fa);
  font-size: 13px;
  color: var(--ds-text-secondary, #565a74);
  display: flex;
  justify-content: space-between;
`;

const SearchResults = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
  }
`;

const SearchResultIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--ds-surface-muted, #f5f7fa);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const SearchResultInfo = styled.div`
  flex: 1;
`;

const SearchResultTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-primary, #212435);
`;

const SearchResultMeta = styled.div`
  font-size: 12px;
  color: var(--ds-text-secondary, #565a74);
`;

const NoResults = styled.div`
  padding: 24px 20px;
  text-align: center;
  color: var(--ds-text-secondary, #565a74);
  font-size: 14px;
`;

// Quick navigation items for search
interface SearchItem {
  id: string;
  title: string;
  path: string;
  type: 'page' | 'template' | 'form' | 'consent';
  icon: string;
}

const quickNavItems: SearchItem[] = [
  { id: 'dashboard', title: 'Dashboard', path: '/admin/dashboard', type: 'page', icon: '📊' },
  { id: 'templates', title: 'Templates', path: '/admin/templates', type: 'page', icon: '📄' },
  { id: 'consent-forms', title: 'Consent Forms', path: '/admin/consent-forms', type: 'page', icon: '📝' },
  { id: 'consents', title: 'Consent Artifacts', path: '/admin/consents', type: 'page', icon: '✅' },
  { id: 'fiduciaries', title: 'Data Fiduciaries', path: '/admin/fiduciaries', type: 'page', icon: '🏢' },
  { id: 'projects', title: 'Projects', path: '/admin/projects', type: 'page', icon: '📁' },
  { id: 'analytics', title: 'Analytics', path: '/admin/analytics', type: 'page', icon: '📈' },
  { id: 'users', title: 'User Management', path: '/admin/users', type: 'page', icon: '👥' },
  { id: 'audit-logs', title: 'Audit Logs', path: '/admin/audit-logs', type: 'page', icon: '📋' },
  { id: 'withdrawals', title: 'Withdrawal Requests', path: '/admin/withdrawals', type: 'page', icon: '🔙' },
  { id: 'tenant-profile', title: 'Tenant Profile', path: '/admin/tenant-profile', type: 'page', icon: '🏛️' },
  { id: 'user-profile', title: 'User Profile', path: '/admin/user-profile', type: 'page', icon: '👤' },
  { id: 'help', title: 'Help & Support', path: '/admin/help', type: 'page', icon: '❓' },
];

// Props
interface TopHeaderProps {
  title?: string;
  breadcrumbs?: { label: string; path?: string }[];
  onMenuClick?: () => void;
}

export const TopHeader: React.FC<TopHeaderProps> = ({ title, breadcrumbs, onMenuClick }) => {
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userInfo, setUserInfo] = useState<{ name?: string; email?: string; role?: string } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem('userInfo');
    if (stored) {
      try {
        setUserInfo(JSON.parse(stored));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter search results
  const searchResults = searchQuery.trim()
    ? quickNavItems.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : quickNavItems.slice(0, 6); // Show first 6 by default

  const handleSearchSelect = (item: SearchItem) => {
    navigate(item.path);
    setSearchOpen(false);
    setSearchQuery('');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const getUserInitials = () => {
    if (userInfo?.name) {
      return userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (userInfo?.email) {
      return userInfo.email[0].toUpperCase();
    }
    return 'A';
  };

  const getDisplayName = () => {
    if (userInfo?.name) return userInfo.name;
    if (userInfo?.email) return userInfo.email.split('@')[0];
    return 'Admin User';
  };

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <MobileMenuButton onClick={onMenuClick}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </MobileMenuButton>

          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumb>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}
                  {crumb.path && index < breadcrumbs.length - 1 ? (
                    <BreadcrumbLink to={crumb.path}>{crumb.label}</BreadcrumbLink>
                  ) : (
                    <BreadcrumbCurrent>{crumb.label}</BreadcrumbCurrent>
                  )}
                </React.Fragment>
              ))}
            </Breadcrumb>
          )}

          {!breadcrumbs && title && <BreadcrumbCurrent>{title}</BreadcrumbCurrent>}
        </LeftSection>

        <RightSection>
          <RegionSelector />

          <TimezonePicker compact />

          <SearchButton onClick={() => setSearchOpen(true)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Search...</span>
            <kbd>⌘K</kbd>
          </SearchButton>

          <NotificationBell />

          <Divider />

          <div style={{ position: 'relative' }} ref={dropdownRef} data-tour="user-menu">
            <UserButton onClick={() => setUserMenuOpen(!userMenuOpen)}>
              <UserAvatar>{getUserInitials()}</UserAvatar>
              <UserName>{getDisplayName()}</UserName>
              <DropdownArrow $open={userMenuOpen}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </DropdownArrow>
            </UserButton>

            <Dropdown $open={userMenuOpen}>
              <DropdownHeader>
                <DropdownUserName>{getDisplayName()}</DropdownUserName>
                <DropdownEmail>{userInfo?.email || 'No email'}</DropdownEmail>
                <DropdownRole>{userInfo?.role || 'ADMIN'}</DropdownRole>
              </DropdownHeader>

              <DropdownMenu>
                <DropdownItem to="/admin/tenant-profile">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Tenant Profile
                </DropdownItem>

                <DropdownItem to="/admin/user-profile">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  User Profile
                </DropdownItem>

                <DropdownDivider />

                <DropdownButton onClick={handleLogout}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </DropdownButton>
              </DropdownMenu>
            </Dropdown>
          </div>
        </RightSection>
      </HeaderContainer>

      {/* Search Modal */}
      <SearchModal $open={searchOpen} onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
        <SearchBox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <SearchInput
            placeholder="Search pages, templates, forms..."
            autoFocus
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          />
          <SearchResults>
            {searchResults.length > 0 ? (
              searchResults.map(item => (
                <SearchResultItem key={item.id} onClick={() => handleSearchSelect(item)}>
                  <SearchResultIcon>{item.icon}</SearchResultIcon>
                  <SearchResultInfo>
                    <SearchResultTitle>{item.title}</SearchResultTitle>
                    <SearchResultMeta>{item.path}</SearchResultMeta>
                  </SearchResultInfo>
                </SearchResultItem>
              ))
            ) : (
              <NoResults>
                No results found for "{searchQuery}"
              </NoResults>
            )}
          </SearchResults>
          <SearchHint>
            <span>{searchQuery ? `${searchResults.length} results` : 'Quick navigation'}</span>
            <span>Press ESC to close</span>
          </SearchHint>
        </SearchBox>
      </SearchModal>
    </>
  );
};

export default TopHeader;
